// lib/flux-3-image.ts
import { closestSupportedAspectRatio } from '#lib/list-generation-aspect-ratio';
import type { Flux3ImageAspectRatio, Flux3ImageResolution } from '#types/types';

export const FLUX_3_IMAGE_MAX_INPUT_IMAGES = 10;

/** fal refuses an input image above 4 megapixels. */
export const FLUX_3_IMAGE_MAX_INPUT_PIXELS = 4_000_000;

export const FLUX_3_IMAGE_DEFAULT_RESOLUTION: Flux3ImageResolution = '2k';

// Owner decision; read by the registry estimator, the only price source.
export const FLUX_3_IMAGE_RESOLUTION_CREDITS: Readonly<Record<Flux3ImageResolution, number>> = {
  '512sq': 1,
  '768sq': 1,
  '1k': 1,
  '2k': 1.5,
  '4k': 2.5,
};

// fal's preset list; BFL's own API also has 9:21, fal does not.
const FLUX_3_IMAGE_SUPPORTED_RATIOS: Readonly<Record<Exclude<Flux3ImageAspectRatio, 'auto'>, number>> = {
  '21:9': 21 / 9, '2:1': 2, '16:9': 16 / 9, '3:2': 3 / 2, '7:5': 7 / 5, '4:3': 4 / 3, '5:4': 5 / 4,
  '1:1': 1, '4:5': 4 / 5, '3:4': 3 / 4, '5:7': 5 / 7, '2:3': 2 / 3, '9:16': 9 / 16, '1:2': 1 / 2,
};

const isOwnKey = <T extends object>(record: T, key: string): key is Extract<keyof T, string> => (
  Object.prototype.hasOwnProperty.call(record, key)
);

/** Only an absent resolution takes the default; anything else must name a tier. */
export const resolveFlux3ImageResolution = (value: unknown): Flux3ImageResolution | null => {
  if (value === undefined) return FLUX_3_IMAGE_DEFAULT_RESOLUTION;
  if (typeof value !== 'string') return null;
  const resolution = value.trim().toLowerCase();
  return isOwnKey(FLUX_3_IMAGE_RESOLUTION_CREDITS, resolution) ? resolution : null;
};

export const parseFlux3ImageAspectRatio = (value: unknown): Flux3ImageAspectRatio | null => {
  if (typeof value !== 'string') return null;
  const ratio = value.trim();
  if (ratio === 'auto') return ratio;
  return isOwnKey(FLUX_3_IMAGE_SUPPORTED_RATIOS, ratio) ? ratio : null;
};

/** The closest fal preset to width:height; a square for a degenerate shape. */
export const deriveFlux3ImageAspectRatio = (width: unknown, height: unknown): Exclude<Flux3ImageAspectRatio, 'auto'> => {
  if (typeof width !== 'number' || typeof height !== 'number') return '1:1';
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return '1:1';
  return closestSupportedAspectRatio(width / height, FLUX_3_IMAGE_SUPPORTED_RATIOS) as Exclude<Flux3ImageAspectRatio, 'auto'>;
};

export const getFlux3ImageEndpoint = (hasImages: boolean): string => (
  `blackforestlabs/flux-3/${hasImages ? 'edit-image' : 'text-to-image'}`
);

export const buildFlux3ImageProviderInput = (input: {
  prompt: string;
  resolution: Flux3ImageResolution;
  aspectRatio: Flux3ImageAspectRatio;
  imageUrls?: readonly string[];
}) => {
  const imageUrls = (input.imageUrls ?? [])
    .filter((url, index, urls): url is string => typeof url === 'string' && url.trim().length > 0 && urls.indexOf(url) === index)
    .slice(0, FLUX_3_IMAGE_MAX_INPUT_IMAGES);
  return {
    prompt: input.prompt,
    aspect_ratio: input.aspectRatio,
    resolution: input.resolution,
    // The provider would reword the prompt the agent wrote.
    enable_prompt_expansion: false,
    safety_tolerance: 4,
    output_format: 'png' as const,
    // A data-URI response would blow up the fal.subscribe payload.
    sync_mode: false,
    ...(imageUrls.length > 0 ? { image_urls: imageUrls } : {}),
  };
};
